import React, { ReactNode, ReactElement, CSSProperties } from 'react';
import styles from './ArrowTooltip.module.css';

/**
 * Enum for tooltip positions.
 */
export enum TooltipPosition {
  TOP_LEFT = 'top-left',
  TOP_CENTER = 'top-center',
  TOP_RIGHT = 'top-right',
  RIGHT = 'right',
  RIGHT_TOP = 'right-top',
  RIGHT_BOTTOM = 'right-bottom',
  LEFT = 'left',
  LEFT_TOP = 'left-top',
  LEFT_BOTTOM = 'left-bottom',
  BOTTOM_LEFT = 'bottom-left',
  BOTTOM_CENTER = 'bottom-center',
  BOTTOM_RIGHT = 'bottom-right',
}

interface ArrowTooltipProps {
  /** Optional `id` for accessibility, testing, and customization */
  id?: string;
  /** The text content inside the tooltip */
  content: string | ReactNode;
  /** The position of the tooltip relative to the trigger element */
  position?: TooltipPosition;
  /** The element that triggers the tooltip on hover */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: ReactElement<any>;
  /** Additional CSS class for customization */
  className?: string;
  /** Optional inline styles for customization */
  style?: CSSProperties;
  showArrow?: boolean;
}

/**
 * **`ArrowTooltip` Component**
 *
 * `ArrowTooltip` component displays a tooltip with an arrow when hovered, focused, or touched.
 * It follows accessibility best practices to support keyboard users and screen readers.
 * Optimized version with pure CSS hover and smooth transitions.
 *
 * @component
 * @param {ArrowTooltipProps} props - Component props
 * @returns {JSX.Element} A styled tooltip with an arrow
 */
const ArrowTooltip: React.FC<ArrowTooltipProps> = ({
  id,
  content,
  position = TooltipPosition.TOP_CENTER,
  children,
  className = '',
  style = {},
  showArrow = true,
}) => {
  return (
    <div className={styles.tooltipContainer}>
      {React.cloneElement(children, {
        'aria-describedby': id,
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          if (typeof children.props.onClick === 'function') {
            children.props.onClick(e);
          }
        },
      })}

      {content && (
        <div
          id={id}
          className={`${styles.tooltip} ${styles[position]} ${className}`}
          style={style}
          role='tooltip'
          aria-live='polite'
        >
          {content}
          {showArrow && <div className={styles.arrow} />}
        </div>
      )}
    </div>
  );
};

export default ArrowTooltip;
